import React from "react";
import { Modal, Table, Tag, Typography, Space, Badge, Card, Empty } from "antd";
import { InboxOutlined, DollarOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const EntregasModal = ({ visible, onClose, entregas = [] }) => {
  // Función para formatear moneda
  const formatCurrency = (valor) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(valor || 0);
  };

  // Función para extraer todos los productos entregados de todas las entregas
  const obtenerProductosEntregados = () => {
    const productos = [];

    entregas.forEach((entrega, entregaIndex) => {
      const { entrega: entregaData } = entrega.entrega;

      entregaData.items.forEach((item, itemIndex) => {
        // Obtener la cantidad entregada de las asignaciones
        const cantidadEntregada =
          item.asignaciones?.reduce((total, asignacion) => {
            return total + (asignacion.cantidad_asignada || 0);
          }, 0) || 0;

        // También podríamos usar balance_items como alternativa
        // const balanceItem = entregaData.balance_items?.find(balance =>
        //   balance.descripcion === item.descripcion_po
        // );
        // const cantidadEntregada = balanceItem?.cantidad || 0;

        productos.push({
          key: `${entrega.id}-${itemIndex}`,
          entrega_id: entrega.id,
          descripcion: item.descripcion_po,
          unidad: item.unidad_po,
          cantidad_disponible: item.cantidad_po, // Cantidad original disponible
          cantidad_entregada: cantidadEntregada, // Cantidad realmente entregada
          valor_unitario: item.valor_unitario,
          valor_total: cantidadEntregada * item.valor_unitario, // Valor basado en cantidad entregada
          numero_orden: item.orden_compra?.numero_orden,
          proveedor: item.orden_compra?.proveedor,
          fecha_po: item.orden_compra?.fecha_po,
        });
      });
    });

    return productos;
  };

  // Columnas para la tabla de productos entregados
  const productosColumns = [
    {
      title: "N° Orden",
      dataIndex: "numero_orden",
      key: "numero_orden",
      width: 120,
      align: "center",
      render: (numero) => (numero ? <Tag color="geekblue">{numero}</Tag> : "-"),
    },
    {
      title: "Proveedor",
      dataIndex: "proveedor",
      key: "proveedor",
      ellipsis: true,
      width: 200,
      render: (proveedor) => proveedor || "-",
    },
    {
      title: "Descripción del Producto",
      dataIndex: "descripcion",
      key: "descripcion",
      ellipsis: true,
      width: 350,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Unidad",
      dataIndex: "unidad",
      key: "unidad",
      width: 120,
      align: "center",
    },
   {
  title: "Cant. Entregada",
  dataIndex: "cantidad_entregada",
  key: "cantidad_entregada",
  width: 110,
  align: "center",
  render: (cantidad) => (
    <Badge
      count={Number(cantidad).toLocaleString("es-CO")}
      overflowCount={999999999} // 👈 evita el +99
      showZero
      style={{ backgroundColor: "#52c41a" }}
    />
  ),
},
    {
      title: "Valor Unitario",
      dataIndex: "valor_unitario",
      key: "valor_unitario",
      width: 140,
      align: "right",
      render: (valor) => <Text type="secondary">{formatCurrency(valor)}</Text>,
    },
    {
      title: "Valor Total",
      dataIndex: "valor_total",
      key: "valor_total",
      width: 140,
      align: "right",
      render: (valor) => (
        <Text strong style={{ color: "#3f8600" }}>
          {formatCurrency(valor)}
        </Text>
      ),
    },
  ];

  const productosEntregados = obtenerProductosEntregados();
  const totalProductos = productosEntregados.length;
  const totalEntregada = productosEntregados.reduce(
    (sum, producto) => sum + producto.cantidad_entregada,
    0
  );
  const valorGrandTotal = productosEntregados.reduce(
    (sum, producto) => sum + producto.valor_total,
    0
  );

  return (
    <Modal
      title={
        <Space>
          <InboxOutlined />
          <span>Productos Entregados</span>
          <Badge count={totalProductos} showZero />
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      style={{ top: 20 }}
    >
      {productosEntregados.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No hay productos entregados para mostrar"
        />
      ) : (
        <div>
          {/* Resumen */}
          <Card className="mb-4" size="small">
            <Space size="large">
              <Space>
                <InboxOutlined style={{ color: "#1890ff" }} />
                <Text>
                  <Text type="secondary">Total productos:</Text>{" "}
                  <Text strong>{totalProductos}</Text>
                </Text>
              </Space>

              <Space>
                <InboxOutlined style={{ color: "#1890ff" }} />
                <Text>
                  <Text type="secondary">Total unidades:</Text>{" "}
                  <Text strong>{totalEntregada}</Text>
                </Text>
              </Space>
              <Space>
                <DollarOutlined style={{ color: "#3f8600" }} />
                <Text>
                  <Text type="secondary">Valor total:</Text>{" "}
                  <Text strong style={{ color: "#3f8600" }}>
                    {formatCurrency(valorGrandTotal)}
                  </Text>
                </Text>
              </Space>
            </Space>
          </Card>

          {/* Tabla de productos */}
          <Table
            dataSource={productosEntregados}
            columns={productosColumns}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} productos`,
            }}
            size="small"
            scroll={{ x: 1100, y: 400 }}
            summary={(pageData) => {
              const totalPagina = pageData.reduce(
                (sum, producto) => sum + producto.valor_total,
                0
              );
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <Text strong>Total de la página:</Text>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={1} align="center">
                      <Text strong style={{ color: "#3f8600" }}>
                        {totalEntregada}
                      </Text>
                    </Table.Summary.Cell>

                    <Table.Summary.Cell index={2}> </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <Text strong style={{ color: "#3f8600" }}>
                        {formatCurrency(totalPagina)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default EntregasModal;
